import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Users, Briefcase, Calendar, DollarSign, CheckCircle, XCircle, FileText, Building2, UserCheck, Activity, Award, Target, Zap, BarChart3, PieChart } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const AnalyticsExecutive = () => {
    const [selectedPeriod, setSelectedPeriod] = useState('thisWeek');
    const [selectedMetric, setSelectedMetric] = useState('profilesCreated');
    const [viewMode, setViewMode] = useState('overview');

    // Mock data for multiple executives
    const executivesData = [
        {
            id: 1,
            name: 'Priya Sharma',
            avatar: 'PS',
            color: '#6366f1',
            rank: 1,
            escalatedCases: 15,
            profilesCreated: 189,
            salonsOnboarded: 18,
            interviewsScheduled: 112,
            jobsPosted: 45,
            applicationsCreated: 298,
            hired: 24,
            hiringCancelled: 4,
            paymentCollected: 67800,
            completeProfiles: 156,
            loginSessions: 1456,
            overallScore: 95
        },
        {
            id: 2,
            name: 'Rahul Verma',
            avatar: 'RV',
            color: '#8b5cf6',
            rank: 2,
            escalatedCases: 23,
            profilesCreated: 147,
            salonsOnboarded: 12,
            interviewsScheduled: 89,
            jobsPosted: 34,
            applicationsCreated: 256,
            hired: 18,
            hiringCancelled: 7,
            paymentCollected: 45600,
            completeProfiles: 182,
            loginSessions: 1247,
            overallScore: 82
        },
        {
            id: 3,
            name: 'Ananya Reddy',
            avatar: 'AR',
            color: '#ec4899',
            rank: 3,
            escalatedCases: 18,
            profilesCreated: 134,
            salonsOnboarded: 15,
            interviewsScheduled: 95,
            jobsPosted: 38,
            applicationsCreated: 267,
            hired: 21,
            hiringCancelled: 5,
            paymentCollected: 54300,
            completeProfiles: 145,
            loginSessions: 1189,
            overallScore: 88
        },
        {
            id: 4,
            name: 'Arjun Patel',
            avatar: 'AP',
            color: '#14b8a6',
            rank: 4,
            escalatedCases: 28,
            profilesCreated: 123,
            salonsOnboarded: 9,
            interviewsScheduled: 76,
            jobsPosted: 29,
            applicationsCreated: 234,
            hired: 15,
            hiringCancelled: 9,
            paymentCollected: 38900,
            completeProfiles: 167,
            loginSessions: 1098,
            overallScore: 75
        },
        {
            id: 5,
            name: 'Sneha Gupta',
            avatar: 'SG',
            color: '#f59e0b',
            rank: 5,
            escalatedCases: 21,
            profilesCreated: 156,
            salonsOnboarded: 14,
            interviewsScheduled: 102,
            jobsPosted: 41,
            applicationsCreated: 289,
            hired: 19,
            hiringCancelled: 6,
            paymentCollected: 51200,
            completeProfiles: 178,
            loginSessions: 1334,
            overallScore: 85
        }
    ];

    const metrics = [
        { key: 'escalatedCases', label: 'Escalated Cases', icon: Activity, color: '#ef4444', lower: true },
        { key: 'profilesCreated', label: 'Profiles Created', icon: Users, color: '#3b82f6' },
        { key: 'salonsOnboarded', label: 'Salons Onboarded', icon: Building2, color: '#8b5cf6' },
        { key: 'interviewsScheduled', label: 'Interviews Scheduled', icon: Calendar, color: '#06b6d4' },
        { key: 'jobsPosted', label: 'Jobs Posted', icon: Briefcase, color: '#f59e0b' },
        { key: 'applicationsCreated', label: 'Applications Created', icon: FileText, color: '#10b981' },
        { key: 'hired', label: 'Successfully Hired', icon: CheckCircle, color: '#22c55e' },
        { key: 'hiringCancelled', label: 'Hiring Cancelled', icon: XCircle, color: '#f43f5e', lower: true },
        { key: 'paymentCollected', label: 'Payment Collected', icon: DollarSign, color: '#14b8a6', prefix: '₹' },
        { key: 'completeProfiles', label: 'Complete Profiles', icon: UserCheck, color: '#6366f1' },
        { key: 'loginSessions', label: 'Login Sessions', icon: Activity, color: '#a855f7' }
    ];

    // Prepare data for charts
    const getBarChartData = () => {
        return executivesData.map(exec => ({
            name: exec.avatar,
            fullName: exec.name,
            hired: exec.hired,
            profilesCreated: exec.profilesCreated,
            salonsOnboarded: exec.salonsOnboarded,
            interviews: exec.interviewsScheduled
        }));
    };

    const getPieChartData = () => {
        return executivesData.map(exec => ({
            name: exec.name,
            value: exec.paymentCollected,
            color: exec.color
        }));
    };

    const getRadarChartData = () => {
        return [
            {
                metric: 'Hired',
                ...executivesData.reduce((acc, exec) => ({ ...acc, [exec.avatar]: exec.hired }), {})
            },
            {
                metric: 'Profiles',
                ...executivesData.reduce((acc, exec) => ({ ...acc, [exec.avatar]: Math.round(exec.profilesCreated / 10) }), {})
            },
            {
                metric: 'Salons',
                ...executivesData.reduce((acc, exec) => ({ ...acc, [exec.avatar]: exec.salonsOnboarded }), {})
            },
            {
                metric: 'Interviews',
                ...executivesData.reduce((acc, exec) => ({ ...acc, [exec.avatar]: Math.round(exec.interviewsScheduled / 5) }), {})
            },
            {
                metric: 'Jobs',
                ...executivesData.reduce((acc, exec) => ({ ...acc, [exec.avatar]: exec.jobsPosted }), {})
            }
        ];
    };

    const getLineChartData = () => {
        const weeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
        return weeks.map((week, idx) => ({
            week,
            ...executivesData.reduce((acc, exec) => ({
                ...acc,
                [exec.avatar]: exec.hired * (0.7 + idx * 0.1)
            }), {})
        }));
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    background: 'rgba(15, 15, 35, 0.95)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    backdropFilter: 'blur(10px)'
                }}>
                    <p style={{ color: 'white', fontWeight: 700, marginBottom: '0.5rem' }}>{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color, fontSize: '0.9rem', margin: '0.25rem 0' }}>
                            {entry.name}: {entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const MetricWiseAnalysis = () => {
        return (
            <div className="metricwise-container">
                {metrics.map((metric, idx) => {
                    const sortedExecs = [...executivesData].sort((a, b) =>
                        metric.lower ? a[metric.key] - b[metric.key] : b[metric.key] - a[metric.key]
                    );
                    const maxValue = Math.max(...executivesData.map(e => e[metric.key] || 0));
                    const minValue = Math.min(...executivesData.map(e => e[metric.key] || 0));
                    const avgValue = executivesData.reduce((sum, e) => sum + (e[metric.key] || 0), 0) / executivesData.length;

                    const chartData = sortedExecs.map(exec => ({
                        name: exec.avatar,
                        fullName: exec.name,
                        value: exec[metric.key] || 0,
                        color: exec.color
                    }));

                    return (
                        <div key={idx} className="metric-analysis-card">
                            <div className="metric-analysis-header">
                                <div className="metric-header-left">
                                    <div className="metric-icon-large" style={{ background: `${metric.color}20`, color: metric.color }}>
                                        <metric.icon size={28} />
                                    </div>
                                    <div>
                                        <h3 style={{ color: metric.color }}>{metric.label}</h3>
                                        <p className="metric-description">
                                            {metric.lower ? 'Lower is better' : 'Higher is better'}
                                        </p>
                                    </div>
                                </div>
                                <div className="metric-stats-summary">
                                    <div className="stat-pill">
                                        <span className="stat-label">Max</span>
                                        <span className="stat-value" style={{ color: metric.color }}>
                                            {metric.prefix || ''}{maxValue > 999 ? maxValue.toLocaleString() : maxValue}
                                        </span>
                                    </div>
                                    <div className="stat-pill">
                                        <span className="stat-label">Avg</span>
                                        <span className="stat-value">{metric.prefix || ''}{Math.round(avgValue)}</span>
                                    </div>
                                    <div className="stat-pill">
                                        <span className="stat-label">Min</span>
                                        <span className="stat-value">{metric.prefix || ''}{minValue}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="metric-analysis-content">
                                <div className="metric-chart-section">
                                    <ResponsiveContainer width="100%" height={250}>
                                        <BarChart data={chartData} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                            <XAxis type="number" stroke="#94a3b8" />
                                            <YAxis dataKey="name" type="category" stroke="#94a3b8" width={50} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>

                                <div className="metric-rankings">
                                    <h4>Rankings</h4>
                                    {sortedExecs.map((exec, rank) => (
                                        <div key={exec.id} className="ranking-item">
                                            <div className="ranking-position">
                                                {rank === 0 && <Award size={18} className="crown" />}
                                                #{rank + 1}
                                            </div>
                                            <div className="exec-avatar-tiny" style={{ background: exec.color }}>
                                                {exec.avatar}
                                            </div>
                                            <span className="ranking-name">{exec.name}</span>
                                            <span className="ranking-value" style={{ color: exec.color }}>
                                                {metric.prefix || ''}{exec[metric.key] > 999 ? exec[metric.key].toLocaleString() : exec[metric.key]}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const ExecutiveCard = ({ executive }) => (
        <div className="executive-card" style={{ '--exec-color': executive.color }}>
            <div className="exec-header">
                <div className="exec-avatar" style={{ background: executive.color }}>
                    {executive.avatar}
                </div>
                <div className="exec-info">
                    <h3 className="exec-name">{executive.name}</h3>
                    <div className="exec-rank">Rank #{executive.rank}</div>
                </div>
                <div className="exec-score">
                    <div className="score-circle" style={{ background: `conic-gradient(${executive.color} ${executive.overallScore * 3.6}deg, rgba(255,255,255,0.1) 0deg)` }}>
                        <div className="score-inner">{executive.overallScore}</div>
                    </div>
                </div>
            </div>

            <div className="exec-metrics-compact">
                {metrics.slice(0, 6).map((metric, idx) => (
                    <div key={idx} className="metric-chip">
                        <metric.icon size={14} style={{ color: metric.color }} />
                        <span className="metric-chip-label">{metric.label.split(' ')[0]}</span>
                        <span className="metric-chip-value">{executive[metric.key]}</span>
                    </div>
                ))}
            </div>
        </div>
    );

    const OverviewDashboard = () => (
        <div className="overview-dashboard">
            <div className="charts-row">
                <div className="chart-card large">
                    <div className="chart-header">
                        <h3>Performance Comparison</h3>
                        <BarChart3 size={20} />
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={getBarChartData()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                            <XAxis dataKey="name" stroke="#94a3b8" />
                            <YAxis stroke="#94a3b8" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="hired" fill="#22c55e" name="Hired" radius={[8, 8, 0, 0]} />
                            <Bar dataKey="salonsOnboarded" fill="#8b5cf6" name="Salons" radius={[8, 8, 0, 0]} />
                            <Bar dataKey="interviews" fill="#06b6d4" name="Interviews" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <h3>Revenue Distribution</h3>
                        <PieChart size={20} />
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <RePieChart>
                            <Pie
                                data={getPieChartData()}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {getPieChartData().map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </RePieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="charts-row">
                <div className="chart-card">
                    <div className="chart-header">
                        <h3>Multi-Metric Radar</h3>
                        <Target size={20} />
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={getRadarChartData()}>
                            <PolarGrid stroke="rgba(255,255,255,0.2)" />
                            <PolarAngleAxis dataKey="metric" stroke="#94a3b8" />
                            <PolarRadiusAxis stroke="#94a3b8" />
                            {executivesData.map((exec, idx) => (
                                <Radar
                                    key={idx}
                                    name={exec.avatar}
                                    dataKey={exec.avatar}
                                    stroke={exec.color}
                                    fill={exec.color}
                                    fillOpacity={0.3}
                                />
                            ))}
                            <Legend />
                            <Tooltip content={<CustomTooltip />} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>

                <div className="chart-card large">
                    <div className="chart-header">
                        <h3>Weekly Hiring Trend</h3>
                        <TrendingUp size={20} />
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={getLineChartData()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                            <XAxis dataKey="week" stroke="#94a3b8" />
                            <YAxis stroke="#94a3b8" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            {executivesData.map((exec, idx) => (
                                <Line
                                    key={idx}
                                    type="monotone"
                                    dataKey={exec.avatar}
                                    stroke={exec.color}
                                    strokeWidth={3}
                                    dot={{ fill: exec.color, r: 5 }}
                                    name={exec.name}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="executives-grid">
                {executivesData.map((exec) => (
                    <ExecutiveCard key={exec.id} executive={exec} />
                ))}
            </div>
        </div>
    );

    const ComparisonTable = () => {
        const getMetricRank = (exec, metricKey) => {
            const metric = metrics.find(m => m.key === metricKey);
            const sorted = [...executivesData].sort((a, b) =>
                metric?.lower ? a[metricKey] - b[metricKey] : b[metricKey] - a[metricKey]
            );
            return sorted.findIndex(e => e.id === exec.id) + 1;
        };

        return (
            <div className="comparison-table-container">
                <table className="comparison-table">
                    <thead>
                        <tr>
                            <th className="sticky-col">Executive</th>
                            {metrics.map((metric, idx) => (
                                <th key={idx}>
                                    <div className="th-content">
                                        <metric.icon size={16} style={{ color: metric.color }} />
                                        <span>{metric.label}</span>
                                    </div>
                                </th>
                            ))}
                            <th>Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {executivesData.map((exec, idx) => (
                            <tr key={idx}>
                                <td className="sticky-col exec-name-cell">
                                    <div className="exec-avatar-small" style={{ background: exec.color }}>
                                        {exec.avatar}
                                    </div>
                                    <span>{exec.name}</span>
                                </td>
                                {metrics.map((metric, mIdx) => {
                                    const rank = getMetricRank(exec, metric.key);
                                    const isTop = rank === 1;
                                    return (
                                        <td key={mIdx} className={isTop ? 'top-performer' : ''}>
                                            <div className="table-cell-content">
                                                {isTop && <Award size={14} className="crown" />}
                                                <span>
                                                    {metric.prefix || ''}{exec[metric.key] && typeof exec[metric.key] === 'number' && exec[metric.key] > 999
                                                        ? exec[metric.key].toLocaleString()
                                                        : exec[metric.key] || 0}
                                                </span>
                                            </div>
                                        </td>
                                    );
                                })}
                                <td>
                                    <div className="score-badge" style={{ background: exec.color }}>
                                        {exec.overallScore}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="dashboard-container">
            <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .dashboard-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%);
          padding: 2rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          position: relative;
          overflow-x: hidden;
        }

        .dashboard-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(168, 85, 247, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .dashboard-header {
          margin-bottom: 2rem;
          position: relative;
          z-index: 1;
        }

        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        h1 {
          font-size: 2.5rem;
          font-weight: 800;
          background: linear-gradient(135deg, #fff 0%, #a78bfa 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -0.02em;
        }

        .header-subtitle {
          color: #94a3b8;
          font-size: 1rem;
          font-weight: 500;
          margin-top: 0.5rem;
        }

        .header-controls {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .control-group {
          display: flex;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.05);
          padding: 0.5rem;
          border-radius: 1rem;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .control-btn {
          padding: 0.75rem 1.5rem;
          border: none;
          background: transparent;
          color: #94a3b8;
          font-weight: 600;
          cursor: pointer;
          border-radius: 0.75rem;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .control-btn.active {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
          box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
        }

        .control-btn:hover:not(.active) {
          background: rgba(255, 255, 255, 0.05);
          color: white;
        }

        .team-overview {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .overview-card {
          background: rgba(255, 255, 255, 0.05);
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
        }

        .overview-label {
          color: #94a3b8;
          font-size: 0.85rem;
          margin-bottom: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .overview-value {
          font-size: 2rem;
          font-weight: 800;
          color: white;
        }

        .overview-dashboard {
          position: relative;
          z-index: 1;
        }

        .charts-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .chart-card {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 1.5rem;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          transition: all 0.4s ease;
        }

        .chart-card:hover {
          transform: translateY(-4px);
          border-color: rgba(99, 102, 241, 0.5);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .chart-card.large {
          grid-column: span 2;
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .chart-header h3 {
          color: white;
          font-size: 1.1rem;
          font-weight: 700;
        }

        .chart-header svg {
          color: #8b5cf6;
        }

        .executives-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .executive-card {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 1.5rem;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          transition: all 0.4s ease;
        }

        .executive-card:hover {
          transform: translateY(-8px);
          border-color: var(--exec-color);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px var(--exec-color);
        }

        .exec-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .exec-avatar {
          width: 56px;
          height: 56px;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 1.2rem;
          color: white;
          flex-shrink: 0;
        }

        .exec-info {
          flex: 1;
        }

        .exec-name {
          color: white;
          font-size: 1.1rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }

        .exec-rank {
          color: #94a3b8;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .exec-score {
          flex-shrink: 0;
        }

        .score-circle {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .score-inner {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #1a1a3e;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 1.1rem;
          color: white;
        }

        .exec-metrics-compact {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        .metric-chip {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 0.75rem;
          transition: all 0.3s ease;
        }

        .metric-chip:hover {
          background: rgba(255, 255, 255, 0.08);
        }

        .metric-chip-label {
          color: #94a3b8;
          font-size: 0.75rem;
          flex: 1;
        }

        .metric-chip-value {
          color: white;
          font-weight: 700;
          font-size: 0.9rem;
        }

        .comparison-table-container {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          overflow-x: auto;
        }

        .comparison-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 1400px;
        }

        .comparison-table th {
          background: rgba(255, 255, 255, 0.08);
          padding: 1.25rem 1rem;
          text-align: left;
          color: #94a3b8;
          font-weight: 600;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .sticky-col {
          position: sticky;
          left: 0;
          background: rgba(26, 26, 62, 0.95);
          z-index: 11;
        }

        .th-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .comparison-table td {
          padding: 1.25rem 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          color: white;
          font-weight: 600;
        }

        .exec-name-cell {
          display: flex;
          align-items: center;
          gap: 1rem;
          font-weight: 700;
        }

        .exec-avatar-small {
          width: 36px;
          height: 36px;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          color: white;
          font-size: 0.85rem;
        }

        .table-cell-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .top-performer {
          background: rgba(255, 215, 0, 0.1);
        }

        .crown {
          color: #ffd700;
        }

        .score-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem 1rem;
          border-radius: 0.75rem;
          font-weight: 800;
          color: white;
          min-width: 50px;
        }

        .metricwise-container {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .metric-analysis-card {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 1.5rem;
          padding: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          transition: all 0.4s ease;
        }

        .metric-analysis-card:hover {
          border-color: rgba(99, 102, 241, 0.5);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .metric-analysis-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1.5rem;
        }

        .metric-header-left {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .metric-icon-large {
          width: 70px;
          height: 70px;
          border-radius: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .metric-analysis-header h3 {
          font-size: 1.5rem;
          font-weight: 800;
          margin-bottom: 0.25rem;
        }

        .metric-description {
          color: #94a3b8;
          font-size: 0.9rem;
        }

        .metric-stats-summary {
          display: flex;
          gap: 1rem;
        }

        .stat-pill {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          padding: 0.75rem 1.25rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 0.75rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .stat-label {
          color: #94a3b8;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 600;
        }

        .stat-value {
          color: white;
          font-size: 1.25rem;
          font-weight: 800;
        }

        .metric-analysis-content {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 2rem;
        }

        .metric-chart-section {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 1rem;
          padding: 1.5rem;
        }

        .metric-rankings {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .metric-rankings h4 {
          color: white;
          font-size: 1.1rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .ranking-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 0.75rem;
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.3s ease;
        }

        .ranking-item:hover {
          background: rgba(255, 255, 255, 0.08);
          transform: translateX(5px);
        }

        .ranking-position {
          width: 40px;
          font-weight: 800;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .ranking-name {
          flex: 1;
          color: white;
          font-weight: 600;
        }

        .ranking-value {
          font-weight: 800;
          font-size: 1.1rem;
        }

        .exec-avatar-tiny {
          width: 32px;
          height: 32px;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          color: white;
          font-size: 0.8rem;
          flex-shrink: 0;
        }

        @media (max-width: 1400px) {
          .chart-card.large {
            grid-column: span 1;
          }

          .metric-analysis-content {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .dashboard-container {
            padding: 1rem;
          }

          h1 {
            font-size: 1.75rem;
          }

          .charts-row {
            grid-template-columns: 1fr;
          }

          .executives-grid {
            grid-template-columns: 1fr;
          }

          .header-top {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>

            <div className="dashboard-header">
                <div className="header-top">
                    <div>
                        <h1>Team Performance Analytics</h1>
                        <div className="header-subtitle">Visual Comparative Analysis · This Week</div>
                    </div>
                    <div className="header-controls">
                        <div className="control-group">
                            <button className="control-btn active">This Week</button>
                            <button className="control-btn">Last Week</button>
                            <button className="control-btn">Last Month</button>
                        </div>
                        <div className="control-group">
                            <button
                                className={`control-btn ${viewMode === 'overview' ? 'active' : ''}`}
                                onClick={() => setViewMode('overview')}
                            >
                                Overview + Charts
                            </button>
                            <button
                                className={`control-btn ${viewMode === 'metricwise' ? 'active' : ''}`}
                                onClick={() => setViewMode('metricwise')}
                            >
                                Metric-Wise Analysis
                            </button>
                            <button
                                className={`control-btn ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >
                                Table View
                            </button>
                        </div>
                    </div>
                </div>

                <div className="team-overview">
                    <div className="overview-card">
                        <div className="overview-label">
                            <Users size={18} />
                            Total Executives
                        </div>
                        <div className="overview-value">{executivesData.length}</div>
                    </div>
                    <div className="overview-card">
                        <div className="overview-label">
                            <CheckCircle size={18} />
                            Total Hired
                        </div>
                        <div className="overview-value">
                            {executivesData.reduce((sum, exec) => sum + exec.hired, 0)}
                        </div>
                    </div>
                    <div className="overview-card">
                        <div className="overview-label">
                            <DollarSign size={18} />
                            Total Revenue
                        </div>
                        <div className="overview-value">
                            ₹{(executivesData.reduce((sum, exec) => sum + exec.paymentCollected, 0) / 1000).toFixed(0)}K
                        </div>
                    </div>
                    <div className="overview-card">
                        <div className="overview-label">
                            <Target size={18} />
                            Avg Score
                        </div>
                        <div className="overview-value">
                            {(executivesData.reduce((sum, exec) => sum + exec.overallScore, 0) / executivesData.length).toFixed(0)}
                        </div>
                    </div>
                </div>
            </div>

            {viewMode === 'overview' && <OverviewDashboard />}
            {viewMode === 'metricwise' && <MetricWiseAnalysis />}
            {viewMode === 'table' && <ComparisonTable />}
        </div>
    );
};

export default AnalyticsExecutive;